
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Raydium Public API endpoint
const RAYDIUM_API = "https://api.raydium.io/v2/main/pairs";

export interface RaydiumPool {
    name: string;
    ammId: string;
    volume24h: number;
    volume24hQuote: number;
    liquidity: number;
    price: number;
    tokenAmountCoin: number;
    tokenAmountPc: number;
    baseMint: string;
    quoteMint: string;
}

export const fetchRaydiumPools = async (): Promise<RaydiumPool[]> => {
    try {
        const res = await fetch(RAYDIUM_API);
        if (!res.ok) throw new Error("Raydium API failed");
        const data = await res.json();
        
        // Return top 20 pools by volume for display
        return data
            .sort((a: any, b: any) => b.volume24h - a.volume24h)
            .slice(0, 20)
            .map((p: any) => ({
                name: p.name,
                ammId: p.ammId,
                volume24h: p.volume24h,
                volume24hQuote: p.volume24hQuote,
                liquidity: p.liquidity,
                price: p.price,
                tokenAmountCoin: p.tokenAmountCoin,
                tokenAmountPc: p.tokenAmountPc,
                baseMint: p.baseMint,
                quoteMint: p.quoteMint
            }));
    } catch (e) {
        console.warn("Using mock Raydium data");
        return getMockRaydiumPools();
    }
}

export const getRaydiumPoolInfo = async (mintAddress: string): Promise<RaydiumPool | null> => {
    // In a real app, filtering the large list or using a specific endpoint is better
    // For now, we mock specific lookups to avoid fetching 5MB JSON every time
    return {
        name: `NOVA-${mintAddress.slice(0,4)}`,
        ammId: 'RaydiumPool_' + mintAddress.slice(0, 6),
        volume24h: Math.random() * 1000000,
        volume24hQuote: Math.random() * 50000,
        liquidity: Math.random() * 500000,
        price: Math.random() * 10,
        tokenAmountCoin: 1000,
        tokenAmountPc: 50,
        baseMint: mintAddress,
        quoteMint: 'So11111111111111111111111111111111111111112',
    };
};

/**
 * Simulates creating a new Raydium Liquidity Pool (CPMM)
 * Called when a token graduates from the bonding curve.
 */
export const createRaydiumPool = async (
    tokenMint: string, 
    tokenAmount: number, 
    solAmount: number
) => {
    console.log(`[Raydium] Creating Pool for ${tokenMint}...`);
    console.log(`[Raydium] Seeding with ${tokenAmount} Tokens + ${solAmount} SOL`);
    
    await new Promise(r => setTimeout(r, 2000)); // Simulate CPMM init

    return {
        poolId: `RAY_${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
        lpMint: `LP_${Math.random().toString(36).slice(2, 10)}`,
        txHash: `5${Math.random().toString(36).slice(2, 60)}`
    };
};

const getMockRaydiumPools = (): RaydiumPool[] => {
    return Array.from({ length: 10 }).map((_, i) => ({
        name: i === 0 ? 'SOL-USDC' : `Pool-${i}`,
        ammId: `amm-${i}`,
        volume24h: 1000000 * (10 - i),
        volume24hQuote: 50000 * (10 - i),
        liquidity: 500000 * (10 - i),
        price: 145 + Math.random(),
        tokenAmountCoin: 5000,
        tokenAmountPc: 200000,
        baseMint: 'So11111111111111111111111111111111111111112',
        quoteMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
    }));
};
